import React, { FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "react-toastify";
import Logo from "shared/Logo/Logo";
import useOutsideAlerter from "hooks/useOutsideAlerter";
import HeroSearchFormSmall, {
  SearchTab,
} from "components/HeroSearchFormSmall/HeroSearchFormSmall";
import { Link, useLocation, useNavigate } from "react-router-dom";
import MenuBar from "shared/MenuBar/MenuBar";
import { StaySearchFormFields } from "components/HeroSearchForm/type";
import { useAppContext } from "AppContext";
import { base_url } from "constant/constant";
import aiSearchIcon from "images/header/ai-search-icon.svg";
import filterIcon from "images/header/filter-icon.svg";
import heartIcon from "images/header/heart-icon.svg";
import backHomeArrow from "images/header/back-home-arrow.svg";
import backArrowIcon from "images/header/back-arrow.svg";
import chevronDownIcon from "images/header/chevron-down.svg";
import minusCircleIcon from "images/header/minus-circle.svg";
import plusCircleIcon from "images/header/plus-circle.svg";
import DatePickerEuropea from "components/Header/DatePickerEuropea";
import { parseFilterDate } from "utils/filterDates";
import parisIcon from "images/header/paris-icon.svg";
import brusselsIcon from "images/header/brussels-icon.svg";
import checkCircleGold from "images/header/check-circle-gold.svg";
import circleOutlineTeal from "images/header/circle-outline-teal.svg";
import MoreFiltersPanel from "components/MoreFiltersPanel/MoreFiltersPanel";
import LanguageSwitcher from "components/Header/LanguageSwitcher";
import { useLocale } from "i18n/locale";
import { buildSearchSummary } from "utils/searchSummary";

const OWNER_URL = "https://www.europeaforowner.com/";

const GUEST_ROWS = [
  { key: "adults", labelKey: "common.adults", descKey: "common.adultsDesc" },
  { key: "children", labelKey: "common.children", descKey: "common.childrenDesc" },
  { key: "infants", labelKey: "common.infants", descKey: "common.infantsDesc" },
] as const;

const LOCATIONS = [
  { id: "Paris", labelKey: "common.paris", icon: parisIcon },
  { id: "Brussels", labelKey: "common.brussels", icon: brusselsIcon },
] as const;

const HeaderEuropea: FC = () => {
  const { t, locale } = useLocale();
  const headerInnerRef = useRef<HTMLDivElement>(null);
  const guestsPopoverRef = useRef<HTMLDivElement>(null);
  const datesPopoverRef = useRef<HTMLDivElement>(null);
  const locationPopoverRef = useRef<HTMLDivElement>(null);
  const [showHeroSearch, setShowHeroSearch] =
    useState<StaySearchFormFields | null>(null);
  const [currentTab, setCurrentTab] = useState<SearchTab>("Stays");
  const [showFilters, setShowFilters] = useState(false);
  // `renderFilterBar()` mounts the location/dates/guests popovers, which use
  // singular refs (see below) for outside-click detection. Its home differs
  // between desktop (inline, centered in the header) and mobile (a separate
  // row below); both copies are gated on the `isDesktop` JS state below (not
  // a Tailwind breakpoint class) so only one is ever mounted — otherwise the
  // shared refs would end up pointing at whichever copy rendered last,
  // breaking outside-click detection on the visible one.
  //
  // Applied via this JS check alone — not a Tailwind breakpoint class — so an
  // arbitrary pixel value can be picked rather than being locked to Tailwind's
  // preset scale.
  //
  // The expanded state (Back to Search + the Location/Dates/Guests/More
  // Filters pill bar) is content-hugging, not flex-shrinkable: its pill
  // fields are fixed-width, so there is a real minimum content width for
  // that row — measured (via a live headless-browser check, not a guess) at
  // ~1140px in English and higher in French (longer labels: "Retour à la
  // recherche", "MENTIONS LÉGALES", "DEVENIR PROPRIÉTAIRE"). Below that
  // minimum, forcing the row to shrink further either leaves dead space
  // inside the pill (when capped with max-width) or — combined with the
  // popover-friendly overflow-visible on renderFilterBar — lets the pill's
  // content spill out and overlap the right nav instead of clipping quietly;
  // neither is acceptable, so this threshold has to clear that minimum with
  // real margin rather than being pushed as low as possible. 1366 is
  // confirmed (live-tested, both languages, both the default and expanded
  // states) to fit cleanly. Below it, the stacked mobile layout (logo row,
  // then a full-width search row below) is used instead — the same
  // fallback mobile devices already use.
  // Live-tested (headless-browser screenshots, not a guess) with the pill
  // fields now genuinely flex-shrinkable (see renderFilterField/renderFilterBar):
  // 1152px is the lowest width that stays clean in French (the tightest
  // language) — fields truncate to "P... / D... / 1..." but nothing overlaps.
  // 1000px overlaps ("Plus de filtres" collides with "MENTIONS LÉGALES").
  // 1152 is used rather than something closer to that 1000px failure point to
  // keep a real safety margin.
  const DESKTOP_HEADER_BREAKPOINT = 1152;
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= DESKTOP_HEADER_BREAKPOINT
  );
  useEffect(() => {
    const update = () => {
      const desktop = window.innerWidth >= DESKTOP_HEADER_BREAKPOINT;
      setIsDesktop((prev) => {
        // Mobile never sets showFilters (it opens MoreFiltersPanel instead),
        // so if a resize crosses down into mobile while it's still true from
        // a desktop session, drop it — otherwise the mobile row would render
        // nothing instead of falling back to the search bar + Filters button.
        if (prev && !desktop) setShowFilters(false);
        return desktop;
      });
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  const [showGuestsPopover, setShowGuestsPopover] = useState(false);
  const [showDatesPopover, setShowDatesPopover] = useState(false);
  const [showLocationPopover, setShowLocationPopover] = useState(false);
  const [aiSearching, setAiSearching] = useState(false);
  // A small grey note shown under the search bar when the AI adjusted the query
  // (e.g. the named landmark is in a different city than the named city).
  const [aiNotice, setAiNotice] = useState<string | null>(null);
  // True once an AI search has actually run, so the result summary shows only
  // after a search — never on a first visit, where the slot stays absent
  // entirely rather than reserving empty space.
  const [aiSearchDone, setAiSearchDone] = useState(false);
  const {
    filterState,
    updateFilterState,
    filterValues,
    setFilterValues,
    locationCounts,
    showMoreFilters,
    setShowMoreFilters,
    aiQuery,
    setAiQuery,
    filteredCount,
  } = useAppContext();
  const navigate = useNavigate();

  // The header is `position: fixed` so it stays pinned in every view — sticky
  // was silently dropping on mobile/tablet because the detail page renders
  // full-bleed `width: 100vw` blocks, and horizontal overflow breaks
  // `position: sticky` on mobile browsers. A fixed element is out of flow, so
  // an equal-height spacer below keeps every page's content from sliding up
  // underneath it. The height is measured (not hardcoded) so it stays correct
  // if the bar wraps on a narrow screen.
  const [headerHeight, setHeaderHeight] = useState(0);
  useLayoutEffect(() => {
    const el = headerInnerRef.current;
    if (!el) return;
    const update = () => setHeaderHeight(el.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  useOutsideAlerter(headerInnerRef, () => {
    setShowHeroSearch(null);
    setCurrentTab("Stays");
  });

  useOutsideAlerter(guestsPopoverRef, () => {
    setShowGuestsPopover(false);
  });

  useOutsideAlerter(datesPopoverRef, () => {
    setShowDatesPopover(false);
  });

  useOutsideAlerter(locationPopoverRef, () => {
    setShowLocationPopover(false);
  });

  const location = useLocation();

  useEffect(() => {
    setShowHeroSearch(null);
    setShowGuestsPopover(false);
    setShowDatesPopover(false);
    setShowLocationPopover(false);
  }, [location]);

  const authPaths = ["/login", "/signup", "/forgot-pass", "/verify-otp"];
  const isAuthPath = authPaths.includes(location.pathname);
  const hiddenSearchPaths = ["/my-bookings", "/legal", "/terms-of-use", "/travel-terms-of-use", "/listing-stay-detail", "/wishlist"];

  // Pages that use the stripped-back header: logo, no search/filters, and a
  // "Back Home" link on the right. Originally the legal pages, now the property
  // detail and wishlist pages too.
  const simpleHeaderPaths = ["/legal", "/terms-of-use", "/travel-terms-of-use", "/listing-stay-detail", "/wishlist"];
  const isSimpleHeader = simpleHeaderPaths.includes(location.pathname);

  const getGuestValue = (key: string): number => {
    switch (key) {
      case "adults": return filterState?.guestNumber || 0;
      case "children": return filterState?.number_of_children || 0;
      case "infants": return filterState?.number_of_infants || 0;
      default: return 0;
    }
  };

  const setGuestValue = (key: string, value: number) => {
    const v = Math.max(0, value);
    switch (key) {
      case "adults": updateFilterState("guestNumber", v); break;
      case "children": updateFilterState("number_of_children", v); break;
      case "infants": updateFilterState("number_of_infants", v); break;
    }
  };

  // Stays search popover removed globally — the search bar is inert and this
  // overlay never renders.
  const renderSearchOverlay = () => null;

  // Fields the AI can fill in on the filterValues side.
  const FILTER_VALUE_KEYS = [
    "propertyType",
    "amenities",
    "propertyRules",
    "safetyMeasures",
    "stayTypes",
  ] as const;

  // The AI search bar is a standalone search, not an incremental tweak like
  // "More Filters" — each submit describes a whole stay from scratch. So a
  // field this box owns that the query doesn't mention must reset to its real
  // default, not keep whatever an earlier, unrelated search left in place.
  // (This bit us: "...in Paris for 2 months" set dates, then a later search
  // with no dates mentioned silently kept them, filtering out otherwise-
  // matching properties that just weren't free on that old date range.)
  const handleAiSearchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const query = aiQuery.trim();
    if (!query || aiSearching) return;

    setAiSearching(true);
    setAiNotice(null);
    setAiSearchDone(false);
    try {
      const response = await axios.post(`${base_url}/ai_search/parse_search_query`, {
        query,
      });

      if (response.data.error) {
        // A `notice` on an error is soft guidance (e.g. "we don't serve India") —
        // show it under the search bar like other notes. A bare error (parse
        // failure, service down) stays a red toast.
        if (response.data.notice) {
          setAiNotice(response.data.notice);
        } else {
          toast.error(response.data.title || t("header.couldNotUnderstand"));
        }
        return;
      }

      // e.g. "Eiffel Tower is in Paris, not Brussels — showing Brussels results."
      setAiNotice(response.data.notice || null);

      const filters = response.data.data || {};

      // null (not a hardcoded city) is this app's own "no location filter"
      // value — the location popover clears a selection the same way, and the
      // backend only filters by city when `location` is truthy. Defaulting
      // this to "Paris" instead would silently hide matching Brussels
      // properties whenever a query doesn't name a city.
      updateFilterState(
        "location",
        filters.location === "Paris" || filters.location === "Brussels"
          ? filters.location
          : null
      );
      // "Near <place>" target. Always set it (falling back to null) so a later
      // search that isn't near anything clears a previous one — same reset
      // discipline as the dates/guests above.
      updateFilterState("nearbyArea", filters.nearbyArea || null);
      // Free-form "near <place>" target for anywhere outside the hardcoded
      // NEIGHBOURHOODS table. Reset to null like the rest, so a later search
      // that isn't near anything releases the previous proximity sort.
      updateFilterState("nearbyPlace", filters.nearbyPlace || null);
      updateFilterState("checkInDate", filters.checkInDate || null);
      updateFilterState("checkOutDate", filters.checkOutDate || null);
      // `|| default`, not a not-mentioned guard: a count of 0 from the AI means
      // "not mentioned" (nobody searches for a 0-guest stay), so it resets to
      // the same default AppContext uses rather than being applied as 0.
      updateFilterState("guestNumber", filters.guestNumber || 1);
      updateFilterState("number_of_children", filters.number_of_children || 0);
      updateFilterState("number_of_infants", filters.number_of_infants || 0);
      updateFilterState("number_of_pets", filters.number_of_pets || 0);

      const valuePatch: Record<string, any> = {};
      FILTER_VALUE_KEYS.forEach((key) => {
        valuePatch[key] = Array.isArray(filters[key]) ? filters[key] : [];
      });
      valuePatch.bedrooms = filters.bedrooms || 0;
      valuePatch.bathrooms = filters.bathrooms || 0;
      valuePatch.toilets = filters.toilets || 0;
      // [0, 1000] matches AppContext's own default range — the slider itself
      // goes to 2000, but "not mentioned" should mean the app's normal
      // no-filter baseline, not the slider's outer limit.
      valuePatch.rangePrices = [
        filters.minPrice || 0,
        filters.maxPrice || 1000,
      ] as unknown as string[];
      setFilterValues({ ...filterValues, ...valuePatch });

      // Reuses the same manual-search trigger the filter bar's "Search" action
      // uses, so a fetch always fires even if none of the watched fields
      // (location/dates/guests) actually changed value.
      updateFilterState("handleSubmit", true);
      // Arms the result summary. It stays hidden until handleSubmit flips back
      // to false (SectionGridHasMap does that once the fetch and filtering are
      // done), so the line never renders against the previous search's count.
      setAiSearchDone(true);

      if (location.pathname !== "/") {
        navigate("/");
      }
    } catch (err) {
      toast.error(t("header.searchUnavailable"));
    } finally {
      setAiSearching(false);
    }
  };

  // Plain-language recap of what the search returned, shown under the bar once
  // results settle. Composed from state the app already holds (filteredCount +
  // the parsed location) rather than asked of the AI: the count has to be exact,
  // and a locally built string is instant, free, and can't invent a number.
  //
  // The city goes through common.paris / common.brussels because filterState
  // holds the backend's enum ("Brussels"), which is "Bruxelles" in French —
  // interpolating it raw would leak the English name into the French sentence.
  const getAiSummary = (): string | null => {
    // Wait for handleSubmit to clear, or the count would still be the previous
    // search's for the moment between submitting and the results landing.
    if (!aiSearchDone || filterState?.handleSubmit) return null;

    return buildSearchSummary({
      count: filteredCount,
      location: filterState?.location ?? null,
      nearbyPlaceName: filterState?.nearbyPlace?.name ?? null,
      filterValues,
      locale,
      t,
    });
  };

  // The notice wins over the summary: it explains something the guest needs to
  // know about their query (an unserved city, a landmark in the other city),
  // which matters more than a count they can also see on the page.
  const aiNoteText = aiNotice || getAiSummary();

  // showNotice=false lets the mobile layout render the note itself, full-width
  // below the whole row, instead of squeezed under the search pill's column.
  // capWidth caps how wide the pill may grow. The bar is flex-1, so without a cap
  // it swallows whatever space the right nav leaves — and the nav's width is
  // language-dependent ("LEGAL | JOIN AS OWNER | EN" is ~140px shorter than
  // "MENTIONS LÉGALES | DEVENIR PROPRIÉTAIRE | FR"), which let the English bar
  // run to ~640px against the French one's ~500. The cap reins that in.
  // 560 rather than a number both languages reach: French tops out at ~502px on
  // its own (a 630px column, less the 12px gap and the 116px Filters button), so
  // this only widens English — which is the point — and leaves ~80px of slack
  // there for justify-center to keep the group centred. Desktop only: the mobile
  // row must still stretch edge to edge. max-w (not w) so the bar can still
  // shrink below it on narrow screens.
  const renderSearchBar = (fullWidth = false, showNotice = true, capWidth = false) => (
    <div
      className={
        fullWidth
          ? `flex-1 min-w-[140px] ${capWidth ? "max-w-[560px]" : ""}`
          : ""
      }
    >
    <form
      onSubmit={handleAiSearchSubmit}
      className={`rounded-[100px] p-[1px] hover:shadow-sm transition-shadow ${
        fullWidth ? "w-full" : ""
      }`}
      style={{ background: "linear-gradient(51.5deg, #B58C56 19.62%, #4C5B58 86.31%)" }}
    >
      <div
        className={`flex items-center gap-[12px] h-[42px] pl-[10px] rounded-[100px] bg-white ${
          fullWidth ? "w-full pr-[16px]" : "pr-[16px]"
        }`}
      >
        <img
          src={aiSearchIcon}
          alt=""
          width={30}
          height={30}
          className={`flex-shrink-0 ${aiSearching ? "animate-spin" : ""}`}
        />
        {/* Mobile keeps a real 16px font (md:text-[12px] on desktop) because iOS
            Safari auto-zooms when a focused input's font-size is under 16px and never
            zooms back out on blur, leaving the results zoomed in. To keep the text
            looking small, it's visually scaled to 85% on mobile (~13.6px) — a transform
            doesn't change the real font-size, so the zoom stays suppressed. Desktop
            uses the plain 12px with no scale. */}
        <input
          type="text"
          value={aiQuery}
          onChange={(e) => {
            setAiQuery(e.target.value);
            // Drop the note and the previous search's summary as soon as the
            // user starts a new query — both describe a search they've moved on
            // from, and the slot collapses again until the next one returns.
            if (aiNotice) setAiNotice(null);
            if (aiSearchDone) setAiSearchDone(false);
          }}
          disabled={aiSearching}
          placeholder={t("header.searchPlaceholder")}
          className={`bg-transparent border-0 outline-none focus:ring-0 focus:ring-offset-0 focus:border-transparent focus:outline-none text-[#445162] text-[16px] md:text-[12px] leading-[24px] origin-left scale-[0.85] md:scale-100 placeholder:text-[#445162] disabled:opacity-60 ${
            fullWidth ? "flex-1 min-w-0" : "w-[480px]"
          }`}
          style={{ fontFamily: "Poppins, sans-serif", fontWeight: 300 }}
        />
      </div>
    </form>
      {/* Rendered only when there's something to say, so the slot takes up no
          space at all before the first search. */}
      {showNotice && aiNoteText && (
        <p
          className="mt-1 px-3 text-[11px] leading-[16px] text-[#445162] line-clamp-2"
          style={{ fontFamily: "Poppins, sans-serif", fontWeight: 300 }}
        >
          {aiNoteText}
        </p>
      )}
    </div>
  );

  const renderFiltersButton = () => (
    <button
      onClick={() => {
        // The inline Back-to-Search + location/dates/guests bar is a
        // desktop-width layout (fixed 560px). On mobile there isn't room for
        // it, so open the MoreFiltersPanel drawer instead — it already has
        // its own Location/Dates/Guests sections plus everything else.
        if (isDesktop) {
          setShowFilters(true);
        } else {
          setShowMoreFilters(true);
        }
      }}
      className="flex items-center justify-center gap-[10px] h-[44px] px-[24px] py-[10px] border border-[#01152E] rounded-[30px] bg-white hover:shadow-sm transition-shadow cursor-pointer flex-shrink-0"
    >
      <img src={filterIcon} alt="" className="flex-shrink-0 w-[14px] h-[10.5px]" />
      <span
        className="text-[#01152E] text-[14px] leading-[24px] whitespace-nowrap"
        style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
      >
        {t("common.filters")}
      </span>
    </button>
  );

  const renderBackToSearchButton = () => (
    <button
      onClick={() => {
        setShowFilters(false);
        setShowHeroSearch(null);
        setShowGuestsPopover(false);
      }}
      className="flex items-center justify-center gap-[8px] h-[44px] px-[16px] border border-[#01152E] rounded-[30px] bg-white hover:shadow-sm transition-shadow cursor-pointer flex-shrink-0"
    >
      <img src={backArrowIcon} alt="" className="w-[13px] h-[12px]" />
      <span
        className="text-[#01152E] text-[14px] leading-[24px] whitespace-nowrap"
        style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
      >
        {t("common.backToSearch")}
      </span>
    </button>
  );

  const getLocationLabel = () => filterState?.location || t("common.location");
  const hasDates = !!(
    parseFilterDate(filterState?.checkInDate) &&
    parseFilterDate(filterState?.checkOutDate)
  );
  const getDatesLabel = () => {
    const checkIn = parseFilterDate(filterState?.checkInDate);
    const checkOut = parseFilterDate(filterState?.checkOutDate);
    if (checkIn && checkOut) {
      const fmt = (d: Date) =>
        d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      return `${fmt(checkIn)} - ${fmt(checkOut)}`;
    }
    return t("common.dates");
  };
  const getGuestsLabel = () => {
    const total =
      (filterState?.guestNumber || 0) +
      (filterState?.number_of_children || 0) +
      (filterState?.number_of_infants || 0);
    return total > 0
      ? `${total} ${total !== 1 ? t("common.guests") : t("common.guest")}`
      : t("common.guests");
  };

  const renderGuestsPopover = () => (
    <div
      ref={guestsPopoverRef}
      className="absolute left-0 top-[48px] w-[255px] bg-white rounded-[12px] px-[20px] py-[24px] flex flex-col gap-[21px] z-50"
      style={{ boxShadow: "0px 0px 10px rgba(0,0,0,0.25)" }}
    >
      {GUEST_ROWS.map((row) => {
        const value = getGuestValue(row.key);
        return (
          <div key={row.key} className="flex items-center justify-between">
            <div className="flex flex-col gap-[4px]">
              <p
                className="text-[14px] leading-[14px] text-[#01152E]"
                style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
              >
                {t(row.labelKey)}
              </p>
              <p
                className="text-[10px] leading-[10px] text-[#445162] whitespace-nowrap"
                style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400 }}
              >
                {t(row.descKey)}
              </p>
            </div>
            <div className="flex items-center gap-[8px] flex-shrink-0">
              <button
                onClick={() => setGuestValue(row.key, value - 1)}
                className="cursor-pointer bg-transparent border-0 p-0 flex"
              >
                <img src={minusCircleIcon} alt="−" width={24} height={24} />
              </button>
              <span
                className="text-[16px] leading-[24px] text-[#01152E] w-[20px] text-center"
                style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
              >
                {value}
              </span>
              <button
                onClick={() => setGuestValue(row.key, value + 1)}
                className="cursor-pointer bg-transparent border-0 p-0 flex"
              >
                <img src={plusCircleIcon} alt="+" width={24} height={24} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderLocationPopover = () => {
    const selected = filterState?.location ?? null;
    return (
      <div
        ref={locationPopoverRef}
        className="absolute left-0 top-[48px] bg-white rounded-[12px] pt-[20px] pb-[16px] flex flex-col items-center z-50"
        style={{
          boxShadow: "0px 0px 10px rgba(0,0,0,0.25)",
          width: "245px",
        }}
      >
        {LOCATIONS.map((loc, i) => (
          <React.Fragment key={loc.id}>
            {i > 0 && (
              <div className="w-full px-[20px] py-[16px]">
                <div className="border-t border-[#d6d6d6]" />
              </div>
            )}
            <button
              onClick={() => {
                updateFilterState("location", selected === loc.id ? null : loc.id);
                setShowLocationPopover(false);
              }}
              className="flex items-center gap-[11px] w-[205px] cursor-pointer bg-transparent border-0 p-0"
            >
              <img
                src={loc.icon}
                alt=""
                width={40}
                height={40}
                className="flex-shrink-0"
              />
              <div className="flex flex-col flex-1">
                <p
                  className="text-[16px] leading-[24px] text-[#01152E] text-left"
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 500,
                  }}
                >
                  {t(loc.labelKey)}
                </p>
                <p
                  className="text-[8px] uppercase tracking-[0.8px] text-[#445162] text-left"
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: 400,
                  }}
                >
                  {locationCounts[loc.id] != null
                    ? `${locationCounts[loc.id]} ${t("common.properties")}`
                    : "\u00A0"}
                </p>
              </div>
              <img
                src={
                  selected === loc.id
                    ? checkCircleGold
                    : circleOutlineTeal
                }
                alt=""
                width={20}
                height={20}
                className="flex-shrink-0"
              />
            </button>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderFilterField = (label: string, field: StaySearchFormFields, hasValue: boolean) => {
    const handleClick = () => {
      if (field === "guests") {
        setShowGuestsPopover((prev) => !prev);
        setShowDatesPopover(false);
        setShowLocationPopover(false);
        setShowHeroSearch(null);
      } else if (field === "dates") {
        setShowDatesPopover((prev) => !prev);
        setShowGuestsPopover(false);
        setShowLocationPopover(false);
        setShowHeroSearch(null);
      } else if (field === "location") {
        setShowLocationPopover((prev) => !prev);
        setShowGuestsPopover(false);
        setShowDatesPopover(false);
        setShowHeroSearch(null);
      } else {
        setShowGuestsPopover(false);
        setShowDatesPopover(false);
        setShowLocationPopover(false);
        setShowHeroSearch(field);
      }
    };

    const isActive =
      (field === "guests" && showGuestsPopover) ||
      (field === "dates" && showDatesPopover) ||
      (field === "location" && showLocationPopover);

    let activeRounding = "";
    if (isActive) {
      if (field === "location") activeRounding = "rounded-l-full";
      else if (field === "guests") activeRounding = "";
    }

    return (
      <div
        key={field}
        className={`relative flex items-center h-full px-[10px] min-w-[56px] flex-1 transition-colors ${
          isActive ? `bg-[#FAF7F1] ${activeRounding}` : ""
        }`}
      >
        {/* Figma pins the label to the field's left edge and the chevron to its
            right — not a centred pair — with ~20px of padding on each side. The
            field's px-[10px] has to stay the floor, though: French fields are
            ~88px against English's ~156px (the longer "Retour à la recherche"
            button eats the bar), and their labels already ellipsize, so a
            hardcoded px-[20px] would just cost them more characters. So the
            extra 10px a side is a capped flexible spacer — it grows to 10px when
            the field has slack (English) and collapses to 0 when it doesn't
            (French). The label is flex-auto, i.e. it starts at its content width
            and only then grows, so it never gives up room to the spacers when
            space is tight; being text-left, the width it does gain shows up as
            the gap before the chevron that Figma has. */}
        <button
          onClick={handleClick}
          className="flex items-center min-w-0 w-full h-full cursor-pointer border-0 px-0 bg-transparent"
        >
          <span aria-hidden className="flex-1 min-w-0 max-w-[10px]" />
          <span
            className={`flex-auto min-w-0 text-left text-[14px] leading-[24px] truncate ${
              hasValue ? "text-[#01152E]" : "text-[#445162]"
            }`}
            style={{ fontFamily: "Poppins, sans-serif", fontWeight: hasValue ? 500 : 300 }}
          >
            {label}
          </span>
          <img src={chevronDownIcon} alt="" className="w-[12px] h-[7.4px] flex-shrink-0 ml-[2px]" />
          <span aria-hidden className="flex-1 min-w-0 max-w-[10px]" />
        </button>
        {field === "guests" && showGuestsPopover && renderGuestsPopover()}
        {field === "dates" && showDatesPopover && (
          <div
            ref={datesPopoverRef}
            className="absolute top-[48px] z-50"
            style={{ left: "50%", transform: "translateX(-50%)" }}
          >
            <DatePickerEuropea onClose={() => setShowDatesPopover(false)} />
          </div>
        )}
        {field === "location" && showLocationPopover && renderLocationPopover()}
      </div>
    );
  };

  const renderFilterBar = () => {
    return (
      <div
        className={`rounded-[100px] p-[1px] ${isDesktop ? "flex-1 min-w-0 overflow-visible" : "w-full overflow-x-auto"}`}
        style={{ background: "linear-gradient(51.5deg, #B58C56 19.62%, #4C5B58 86.31%)" }}
      >
        <div className="flex items-stretch h-[42px] rounded-[100px] bg-white">
            {renderFilterField(getLocationLabel(), "location", !!filterState?.location)}
            <div className="w-0 flex items-center">
              <div className="h-full border-l border-[#d6d6d6]" />
            </div>

            {renderFilterField(
              getDatesLabel(),
              "dates",
              hasDates
            )}
            <div className="w-0 flex items-center">
              <div className="h-full border-l border-[#d6d6d6]" />
            </div>

            {renderFilterField(
              getGuestsLabel(),
              "guests",
              (filterState?.guestNumber || 0) +
                (filterState?.number_of_children || 0) +
                (filterState?.number_of_infants || 0) >
                0
            )}
            <div className="w-0 flex items-center">
              <div className="h-full border-l border-[#d6d6d6]" />
            </div>

            {/* More Filters */}
            <button
              onClick={() => setShowMoreFilters(true)}
              className="flex items-center justify-center gap-[6px] px-[14px] cursor-pointer bg-transparent border-0"
            >
              <img src={filterIcon} alt="" className="w-[14px] h-[10.5px] flex-shrink-0" />
              <span
                className="flex-1 text-left text-[14px] leading-[24px] text-[#01152E] whitespace-nowrap"
                style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
              >
                {t("common.moreFilters")}
              </span>
            </button>
        </div>
      </div>
    );
  };

  const renderRightNav = () => (
    <div className="hidden md:flex items-center gap-2 lg:gap-[8px] flex-shrink-0">
      {isSimpleHeader && (
        <Link
          to="/"
          className="hover:opacity-80 transition-opacity"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 24px",
            border: "1px solid #01152E",
            borderRadius: 30,
            background: "white",
            textDecoration: "none",
          }}
        >
          <img src={backHomeArrow} alt="" style={{ width: 13, height: 12 }} />
          <span
            style={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: 500,
              fontSize: 14,
              lineHeight: "24px",
              color: "#01152E",
              whiteSpace: "nowrap",
            }}
          >
            {t("common.backHome")}
          </span>
        </Link>
      )}
      <Link to="/wishlist" className="hover:opacity-70 transition-opacity" title={t("common.wishlist")}>
        <img src={heartIcon} alt={t("common.wishlist")} width={20} height={18} />
      </Link>

      <div className="h-[12px] w-px bg-[#D6D6D6]" />

      <Link
        to="/legal"
        className="text-[12px] uppercase tracking-[1.2px] text-[#01152E] hover:opacity-70 transition-opacity"
        style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
      >
        {t("common.legal")}
      </Link>

      <div className="h-[12px] w-px bg-[#D6D6D6]" />

      <a
        href={OWNER_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[12px] uppercase tracking-[1.2px] text-[#01152E] hover:opacity-70 transition-opacity"
        style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
      >
        {t("common.joinAsOwner")}
      </a>

      <div className="h-[12px] w-px bg-[#D6D6D6]" />

      <LanguageSwitcher />
    </div>
  );

  return (
    <>
      {/* The header is portaled to <body> and positioned `fixed` so it pins to
          the viewport in every view. Rendering it in place left it a descendant
          of the routed page; on the detail page that page's full-bleed
          `width: 100vw` blocks were enough to drop `position: sticky` (and can
          trap `fixed`) on mobile, so the bar scrolled away. A body-level portal
          escapes that entirely. */}
      {createPortal(
        <>
          {/* Dark overlay when search is open */}
          <div
        className={`fixed z-40 inset-0 bg-black/30 transition-opacity will-change-[opacity] ${
          showHeroSearch
            ? "visible"
            : "invisible opacity-0 pointer-events-none"
        }`}
      />

      <header
        ref={headerInnerRef}
        className="fixed top-0 inset-x-0 z-40 bg-white"
      >
        <div className="px-4 sm:px-6 lg:px-[40px] py-[16px]">
          {/* Background that scales when search is expanded */}
          <div
            className={`bg-white absolute h-full inset-x-0 top-0 transition-transform will-change-[transform,opacity] ${
              showHeroSearch ? "duration-75 scale-y-[3.4]" : ""
            }`}
          />

          <div className="relative flex items-start justify-between">
            {/* Left: Logo — kept centred in the 44px bar band even when the AI
                note grows the row below it. */}
            <div className="relative z-10 flex-shrink-0 h-[44px] flex items-center">
              <Logo />
            </div>

            {/* Center: Search + Filters (desktop) / Mobile search */}
            {!isAuthPath && (
              // 32px left / 16px right rather than a symmetric mx-6: the row
              // should keep clear of the logo but can sit closer to the right
              // nav. It matters most where the content fills this column edge to
              // edge and the margins are the only separation — the filter bar in
              // either language, and the search bar in French, whose longer nav
              // labels leave it no slack. The total is unchanged at 48px, so the
              // content keeps its width and simply shifts 8px right; where there
              // IS slack (the English search bar) justify-center still centres
              // the group within these bounds.
              <div className="flex-1 flex justify-center min-w-0 ml-8 mr-4">
                {/* Desktop */}
                {/* items-start (not center): the AI-search bar can grow a note
                    line below it, and the Filters button must stay aligned with
                    the search pill's top row, not drop to the taller box's centre.
                    justify-center centres the search+Filters group in the gap
                    between the logo and the right nav. It only bites once the
                    search bar stops growing at its max-w cap: the leftover space
                    (in English, ~140px the shorter nav frees up) then splits
                    evenly on both sides instead of all pooling after Filters.
                    w-full min-w-0 so this row (and the flex-1/min-w-0 search bar
                    or filter bar inside it) can actually shrink instead of
                    forcing the header wider than the viewport — a plain flex
                    item's default min-width:auto blocks shrinking below its
                    content's natural size otherwise. In the filter-bar branch,
                    both the wrapper and renderFilterBar itself are flex-1
                    min-w-0, and each pill field inside renderFilterField is
                    flex-1 min-w-[56px] with a truncating label — so the whole
                    assembly compresses smoothly (fields truncate their text)
                    down to a real floor instead of either leaving dead space or
                    letting content spill out and overlap the right nav. */}
                {isDesktop && (
                  <div className="flex items-start justify-center gap-[12px] w-full min-w-0">
                    {!hiddenSearchPaths.includes(
                      location.pathname
                    ) && (
                      <>
                        {showFilters ? (
                          <div className="flex items-center gap-[10px] w-full min-w-0">
                            {renderBackToSearchButton()}
                            {renderFilterBar()}
                          </div>
                        ) : (
                          <>
                            {renderSearchBar(true, true, true)}
                            {renderFiltersButton()}
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}

                {renderSearchOverlay()}
              </div>
            )}

            {/* Right: Heart, Legal, Join as Owner, Menu — same fixed 44px band as
                the Logo, so these stay centred on the search bar / Filters line
                instead of riding up to the top of a row the AI note has grown. */}
            <div className="relative z-10 flex-shrink-0 h-[44px] flex items-center gap-3">
              {renderRightNav()}
              {/* Mobile: language switcher + hamburger (shown only on mobile when
                  the desktop right nav is hidden) */}
              <div className="md:hidden flex items-center gap-1">
                <LanguageSwitcher />
                <MenuBar login={false} />
              </div>
            </div>
          </div>

          {/* Mobile: AI search bar + Filters, below the logo/hamburger row */}
          {!isDesktop && !isAuthPath && !hiddenSearchPaths.includes(location.pathname) && (
            // items-start so the note under the search bar doesn't drop the
            // Filters button below the pill — see the desktop row above.
            <div className="relative mt-[12px]">
              <div className="flex items-start gap-[12px]">
                {showFilters ? (
                  <>
                    {renderBackToSearchButton()}
                    {renderFilterBar()}
                  </>
                ) : (
                  <>
                    {/* Note is rendered below, full-width — not squeezed under
                        the pill's column — so it uses the space beside Filters. */}
                    {renderSearchBar(true, false)}
                    {renderFiltersButton()}
                  </>
                )}
              </div>
              {!showFilters && aiNoteText && (
                <p
                  className="mt-1 px-3 text-[11px] leading-[16px] text-[#445162] line-clamp-2"
                  style={{ fontFamily: "Poppins, sans-serif", fontWeight: 300 }}
                >
                  {aiNoteText}
                </p>
              )}
            </div>
          )}
        </div>
      </header>
        </>,
        document.body
      )}

      {/* In-flow spacer that reserves the portaled fixed header's height, so no
          page's content slides up underneath it. */}
      <div aria-hidden style={{ height: headerHeight }} />

      <MoreFiltersPanel
        isOpen={showMoreFilters}
        onClose={() => setShowMoreFilters(false)}
      />
    </>
  );
};

export default HeaderEuropea;
